/* ============================================================
   ÆLOA - global.js
   Vanilla, dependency-free storefront behaviour.
   Modules: overlays · AJAX cart · quantity steppers · slideshow ·
   back-to-top · predictive search · welcome popup · cookie consent ·
   product buy-box. All motion stays slow & quiet (brand rule).
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.aeloa || {};
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  /* ---------- Money ---------- */
  // Handles every Shopify money placeholder, not just {{amount}}. The Danish
  // shop format is {{amount_with_comma_separator}}, which the old regex missed
  // - leaving the raw placeholder on screen instead of a price.
  function formatMoney(cents) {
    var fmt = CFG.moneyFormat || '{{amount}} kr';
    function group(thousands, decimal, decimals) {
      var n = Math.abs(cents) / 100;
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (cents < 0 ? '-' : '') + (parts[1] ? parts[0] + decimal + parts[1] : parts[0]);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount': return group(',', '.', 2);
        case 'amount_no_decimals': return group(',', '.', 0);
        case 'amount_with_comma_separator': return group('.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return group('.', ',', 0);
        case 'amount_with_apostrophe_separator': return group("'", '.', 2);
        case 'amount_with_space_separator': return group(' ', ',', 2);
        case 'amount_no_decimals_with_space_separator': return group(' ', ',', 0);
        default: return group(',', '.', 2);
      }
    });
  }

  /* =========================================================
     OVERLAYS  (cart drawer · search · mobile menu · account · popup)
     ========================================================= */
  var OverlayManager = {
    open: function (name) {
      var el = $('[data-overlay="' + name + '"]');
      if (!el) return;
      el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      this._active = el;
      var focusable = el.querySelector('[autofocus], input, button, a');
      if (focusable) { try { focusable.focus({ preventScroll: true }); } catch (e) {} }
      document.dispatchEvent(new CustomEvent('aeloa:overlay:open', { detail: { name: name } }));
    },
    close: function (el) {
      el = el || this._active;
      if (!el) return;
      el.setAttribute('aria-hidden', 'true');
      if (!$('[aria-hidden="false"].overlay')) document.body.classList.remove('no-scroll');
      this._active = null;
      document.dispatchEvent(new CustomEvent('aeloa:overlay:close'));
    },
    closeAll: function () { $$('.overlay[aria-hidden="false"]').forEach(this.close.bind(this)); }
  };

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-overlay-open]');
    if (opener) {
      e.preventDefault();
      OverlayManager.open(opener.getAttribute('data-overlay-open'));
      return;
    }
    if (e.target.closest('[data-overlay-close]') || e.target.classList.contains('overlay__scrim')) {
      var overlay = e.target.closest('.overlay');
      OverlayManager.close(overlay);
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') OverlayManager.closeAll();
  });

  /* =========================================================
     QUANTITY STEPPERS
     ========================================================= */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    var wrap = btn.closest('[data-qty]');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    var min = parseInt(input.getAttribute('min') || '1', 10);
    var val = parseInt(input.value || min, 10);
    val = btn.hasAttribute('data-qty-plus') ? val + 1 : Math.max(min, val - 1);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* =========================================================
     AJAX CART
     ========================================================= */
  var Cart = {
    sectionsToRender: ['cart-drawer'],

    add: function (form) {
      var data = new FormData(form);
      data.append('sections', this.sectionsToRender.join(','));
      data.append('sections_url', window.location.pathname);
      var submit = form.querySelector('[type="submit"]');
      if (submit) { submit.setAttribute('disabled', ''); submit.classList.add('is-loading'); }

      return fetch(CFG.routes.cart_add, {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) { Cart._error(form, res.description || res.message); return; }
          Cart._renderSections(res.sections);
          Cart.refreshCount();
          if (CFG.cartType === 'drawer') OverlayManager.open('cart');
          else window.location = CFG.routes.cart;
          document.dispatchEvent(new CustomEvent('aeloa:cart:added', { detail: res }));
        })
        .catch(function () { Cart._error(form); })
        .finally(function () { if (submit) { submit.removeAttribute('disabled'); submit.classList.remove('is-loading'); } });
    },

    change: function (line, quantity) {
      return fetch(CFG.routes.cart_change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: this.sectionsToRender.join(','), sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          Cart._renderSections(res.sections);
          Cart.refreshCount(res.item_count);
          document.dispatchEvent(new CustomEvent('aeloa:cart:updated', { detail: res }));
        });
    },

    addById: function (id, qty) {
      var data = new FormData();
      data.append('id', id);
      data.append('quantity', qty || 1);
      data.append('sections', this.sectionsToRender.join(','));
      data.append('sections_url', window.location.pathname);
      return fetch(CFG.routes.cart_add, {
        method: 'POST', headers: { 'Accept': 'application/javascript' }, body: data
      }).then(function (r) { return r.json(); }).then(function (res) {
        Cart._renderSections(res.sections);
        Cart.refreshCount();
        OverlayManager.open('cart');
      });
    },

    _renderSections: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var parsed = new DOMParser().parseFromString(sections[id], 'text/html');
        var newContent = parsed.querySelector('[data-cart-contents]');
        var current = $('[data-cart-contents]');
        if (newContent && current) current.innerHTML = newContent.innerHTML;
      });
    },

    refreshCount: function (count) {
      var update = function (n) {
        $$('[data-cart-count]').forEach(function (el) { el.textContent = n; el.classList.toggle('is-empty', n < 1); });
      };
      if (typeof count === 'number') { update(count); return; }
      fetch(CFG.routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { update(c.item_count); });
    },

    _error: function (form, msg) {
      var box = form && form.querySelector('[data-cart-error]');
      if (box) { box.textContent = msg || 'Noget gik galt. Prøv igen.'; box.hidden = false; }
    }
  };

  // Intercept product add-to-cart forms
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    Cart.add(form);
  });

  // Drawer line quantity changes + removals + cross-sell adds
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-line] input');
    if (!input) return;
    var line = input.closest('[data-cart-line]').getAttribute('data-cart-line');
    Cart.change(line, parseInt(input.value || '0', 10));
  });
  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-cart-remove]');
    if (remove) { e.preventDefault(); Cart.change(remove.getAttribute('data-cart-remove'), 0); return; }
    var add = e.target.closest('[data-cart-add-id]');
    if (add) { e.preventDefault(); Cart.addById(add.getAttribute('data-cart-add-id'), 1); return; }
    var multi = e.target.closest('[data-add-multiple]');
    if (multi) {
      e.preventDefault();
      var ids;
      try { ids = JSON.parse(multi.getAttribute('data-add-multiple')); } catch (err) { return; }
      var items = ids.filter(Boolean).map(function (id) { return { id: id, quantity: 1 }; });
      fetch(CFG.routes.cart_add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify({ items: items, sections: Cart.sectionsToRender.join(','), sections_url: window.location.pathname })
      }).then(function (r) { return r.json(); }).then(function (res) {
        Cart._renderSections(res.sections);
        Cart.refreshCount();
        OverlayManager.open('cart');
      });
    }
  });

  /* =========================================================
     SLIDESHOW  (hero)
     ========================================================= */
  function initSlideshow(root) {
    var slides = $$('[data-slide]', root);
    if (slides.length < 2) return;
    var dots = $$('[data-slide-dot]', root);
    var interval = parseInt(root.getAttribute('data-autoplay') || '6000', 10);
    var idx = 0, timer = null, paused = false;

    function show(n) {
      idx = (n + slides.length) % slides.length;
      slides.forEach(function (s, i) {
        s.style.opacity = i === idx ? '1' : '0';
        s.style.zIndex = i === idx ? '2' : '1';
        s.style.pointerEvents = i === idx ? 'auto' : 'none';
      });
      dots.forEach(function (d, i) { d.setAttribute('aria-current', i === idx ? 'true' : 'false'); });
    }
    function next() { show(idx + 1); }
    function prev() { show(idx - 1); }
    function play() { if (interval > 0) timer = setInterval(function () { if (!paused) next(); }, interval); }
    function stop() { if (timer) clearInterval(timer); }

    $$('[data-slide-next]', root).forEach(function (b) { b.addEventListener('click', function () { next(); }); });
    $$('[data-slide-prev]', root).forEach(function (b) { b.addEventListener('click', function () { prev(); }); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { show(i); }); });
    document.addEventListener('aeloa:overlay:open', function () { paused = true; });
    document.addEventListener('aeloa:overlay:close', function () { paused = false; });

    show(0); play();
  }

  /* =========================================================
     BACK TO TOP
     ========================================================= */
  function initBackToTop() {
    var btn = $('[data-back-to-top]');
    if (!btn) return;
    var onScroll = function () { btn.classList.toggle('is-visible', window.scrollY > 640); };
    window.addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: 'smooth' }); });
    onScroll();
  }

  /* =========================================================
     PREDICTIVE SEARCH
     ========================================================= */
  function initPredictiveSearch() {
    var input = $('[data-predictive-input]');
    var results = $('[data-predictive-results]');
    if (!input || !results || !CFG.routes.predictive_search) return;
    var t = null;
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(t);
      if (q.length < 2) { results.innerHTML = ''; return; }
      t = setTimeout(function () {
        var url = CFG.routes.predictive_search + '?q=' + encodeURIComponent(q) +
          '&resources[type]=product,article&resources[limit]=6&section_id=predictive-search';
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var inner = parsed.querySelector('[data-predictive-results]');
            results.innerHTML = inner ? inner.innerHTML : '';
          });
      }, 250);
    });
  }

  /* =========================================================
     WELCOME POPUP + COOKIE CONSENT  (localStorage gated)
     ========================================================= */
  function ls(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, val) { try { localStorage.setItem(key, val); } catch (e) {} }

  function initPopup() {
    var popup = $('[data-overlay="popup"]');
    if (!popup) return;
    var delay = parseInt(popup.getAttribute('data-popup-delay') || '2200', 10);
    if (ls('aeloa-popup') === 'seen') return;
    setTimeout(function () { OverlayManager.open('popup'); }, delay);
    popup.addEventListener('click', function (e) {
      if (e.target.closest('[data-overlay-close]') || e.target.classList.contains('overlay__scrim')) lsSet('aeloa-popup', 'seen');
    });
    var form = popup.querySelector('form');
    if (form) form.addEventListener('submit', function () { lsSet('aeloa-popup', 'seen'); });
  }

  function initCookie() {
    var bar = $('[data-cookie]');
    if (!bar) return;
    if (ls('aeloa-cookie') === 'set') { bar.hidden = true; return; }
    bar.hidden = false;
    $$('[data-cookie-accept]', bar).forEach(function (b) {
      b.addEventListener('click', function () { lsSet('aeloa-cookie', 'set'); bar.hidden = true; });
    });
  }

  /* =========================================================
     PRODUCT BUY-BOX  (variants · gift-wrap · live total)
     ========================================================= */
  function initBuyBox(root) {
    var form = root.querySelector('form[action*="/cart/add"]') || $('[data-product-form]', root);
    if (!form) return;
    var data = $('[data-product-json]', root);
    var product = data ? JSON.parse(data.textContent) : null;
    if (!product) return;

    var variantInput = form.querySelector('[name="id"]');
    var priceEl = $('[data-buy-price]', root);
    var labelTotalEl = $('[data-buy-total]', root);
    var qtyInput = form.querySelector('[name="quantity"]');
    var giftToggle = $('[data-gift-toggle]', root);
    var giftPrice = parseInt(root.getAttribute('data-gift-price') || '0', 10) * 100;

    function currentVariant() {
      var selected = {};
      $$('[data-option-index]', root).forEach(function (group) {
        var checked = group.querySelector('input:checked, [aria-pressed="true"]');
        if (checked) selected[group.getAttribute('data-option-index')] = checked.value || checked.getAttribute('data-value');
      });
      return product.variants.find(function (v) {
        return Object.keys(selected).every(function (i) { return v.options[i] === selected[i]; });
      }) || product.variants[0];
    }

    function render() {
      var v = currentVariant();
      if (!v) return;
      if (variantInput) variantInput.value = v.id;
      document.dispatchEvent(new CustomEvent('aeloa:variant:render', { detail: { variant: v, root: root } }));
      var qty = parseInt(qtyInput && qtyInput.value || '1', 10);
      var gift = giftToggle && giftToggle.getAttribute('aria-pressed') === 'true' ? giftPrice : 0;
      var total = v.price * qty + gift;
      if (priceEl) priceEl.textContent = formatMoney(v.price);
      if (labelTotalEl) labelTotalEl.textContent = formatMoney(total);
      // No stock messaging (store rule - CLAUDE.md §7): the submit button is
      // never disabled or relabelled "Udsolgt" on variant change.
    }

    // option chips
    $$('[data-option-chip]', root).forEach(function (chip) {
      chip.addEventListener('click', function () {
        var group = chip.closest('[data-option-index]');
        $$('[data-option-chip]', group).forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
        chip.setAttribute('aria-pressed', 'true');
        var label = group.querySelector('[data-option-current]');
        if (label) label.textContent = chip.getAttribute('data-value');
        render();
      });
    });
    if (giftToggle) {
      giftToggle.addEventListener('click', function () {
        var on = giftToggle.getAttribute('aria-pressed') === 'true';
        giftToggle.setAttribute('aria-pressed', on ? 'false' : 'true');
        var prop = form.querySelector('[name="properties[Gaveindpakning]"]');
        if (prop) prop.disabled = on; // enabled when turning ON
        render();
      });
    }
    form.addEventListener('change', render);
    if (qtyInput) qtyInput.addEventListener('input', render);
    render();
  }

  /* =========================================================
     PRODUCT TABS
     ========================================================= */
  function initTabs(root) {
    var tabs = $$('[data-tab]', root);
    if (!tabs.length) return;
    function activate(id) {
      tabs.forEach(function (t) { t.setAttribute('aria-selected', t.getAttribute('data-tab') === id ? 'true' : 'false'); });
      $$('[data-tab-panel]', root).forEach(function (p) { p.hidden = p.getAttribute('data-tab-panel') !== id; });
    }
    tabs.forEach(function (t) { t.addEventListener('click', function () { activate(t.getAttribute('data-tab')); }); });
    document.addEventListener('aeloa:tabs:select', function (e) { activate(e.detail.id); });
    activate(tabs[0].getAttribute('data-tab'));
  }

  /* =========================================================
     INIT
     ========================================================= */
  function init(scope) {
    scope = scope || document;
    $$('[data-slideshow]', scope).forEach(initSlideshow);
    $$('[data-product-root]', scope).forEach(initBuyBox);
    // Tabs are their own section ([data-tabs-root]) - scoping them to
    // [data-product-root] found no [data-tab] and silently did nothing.
    $$('[data-tabs-root]', scope).forEach(initTabs);
    initBackToTop();
    initPredictiveSearch();
    initPopup();
    initCookie();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else { init(); }

  // Re-init sections edited in the theme editor
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.aeloaCart = Cart;
  window.aeloaOverlay = OverlayManager;
})();
